import { useMemo, useRef } from 'react';
import { isMouseLikePointerType, isTypeableElement } from '@floating-ui/react/utils';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getWindow(node) {
    return node?.ownerDocument?.defaultView || window;
}
function isHTMLElement(value) {
    return value instanceof HTMLElement || value instanceof getWindow(value).HTMLElement;
}
function isButtonTarget(event) {
    return isHTMLElement(event.target) && event.target.tagName === 'BUTTON';
}
function isSpaceIgnored(element) {
    return isTypeableElement(element);
}
/**
 * Opens or closes the floating element when clicking the reference element.
 * @param context
 * @param props
 * @see https://floating-ui.com/docs/useClick
 */
const MOUSE_BUTTONS = ['click', 'middle-click', 'right-click'];
export function useExtendedClick(context, props = {}) {
    const { open, onOpenChange, dataRef, elements: { domReference }, } = context;
    const { enabled = true, event: eventOption = 'click', toggle = true, ignoreMouse = false, keyboardHandlers = true } = props;
    const pointerTypeRef = useRef();
    const didKeyDownRef = useRef(false);
    return useMemo(() => {
        if (!enabled)
            return {};
        return {
            reference: {
                onPointerDown(event) {
                    pointerTypeRef.current = event.pointerType;
                },
                onMouseDown(event) {
                    // Ignore all buttons except for the "main" button.
                    // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
                    if (event.button !== MOUSE_BUTTONS.indexOf(eventOption)) {
                        return;
                    }
                    if (isMouseLikePointerType(pointerTypeRef.current, true) && ignoreMouse) {
                        return;
                    }
                    if (eventOption === 'click') {
                        return;
                    }
                    if (open && toggle && (dataRef.current.openEvent ? dataRef.current.openEvent.type === 'mousedown' : true)) {
                        onOpenChange(false, event.nativeEvent, 'click');
                    }
                    else {
                        // Prevent stealing focus from the floating element
                        event.preventDefault();
                        onOpenChange(true, event.nativeEvent, 'click');
                    }
                },
                onClick(event) {
                    if (eventOption === 'mousedown' && pointerTypeRef.current) {
                        pointerTypeRef.current = undefined;
                        return;
                    }
                    if (isMouseLikePointerType(pointerTypeRef.current, true) && ignoreMouse) {
                        return;
                    }
                    if (open && toggle && (dataRef.current.openEvent ? dataRef.current.openEvent.type === 'click' : true)) {
                        onOpenChange(false, event.nativeEvent, 'click');
                    }
                    else {
                        onOpenChange(true, event.nativeEvent, 'click');
                    }
                },
                onKeyDown(event) {
                    pointerTypeRef.current = undefined;
                    if (event.defaultPrevented || !keyboardHandlers || isButtonTarget(event)) {
                        return;
                    }
                    if (event.key === ' ' && !isSpaceIgnored(domReference)) {
                        // Prevent scrolling
                        event.preventDefault();
                        didKeyDownRef.current = true;
                    }
                    if (event.key === 'Enter') {
                        if (open && toggle) {
                            onOpenChange(false, event.nativeEvent, 'click');
                        }
                        else {
                            onOpenChange(true, event.nativeEvent, 'click');
                        }
                    }
                },
                onKeyUp(event) {
                    if (event.defaultPrevented || !keyboardHandlers || isButtonTarget(event) || isSpaceIgnored(domReference)) {
                        return;
                    }
                    if (event.key === ' ' && didKeyDownRef.current) {
                        didKeyDownRef.current = false;
                        if (open && toggle) {
                            onOpenChange(false, event.nativeEvent, 'click');
                        }
                        else {
                            onOpenChange(true, event.nativeEvent, 'click');
                        }
                    }
                },
                onContextMenu(event) {
                    if (eventOption === 'right-click') {
                        event.preventDefault();
                    }
                },
            },
        };
    }, [enabled, dataRef, eventOption, ignoreMouse, keyboardHandlers, domReference, toggle, open, onOpenChange]);
}
